/**
 * <delivery-promise> — cutoff-aware delivery message, rendered by snippets/delivery-promise.liquid.
 *
 * - mode "arrive" (next-day countries, NL/BE): "Voor 15:00 uur besteld, morgen in huis"
 *   After the cutoff or outside working days: "Nu besteld, woensdag in huis"
 * - mode "ship" (other countries): "Voor 15:00 uur besteld, vandaag verzonden · Levering in 2–4 werkdagen"
 *   After the cutoff or outside working days: "Nu besteld, maandag verzonden · Levering in 2–4 werkdagen"
 *
 * Time is evaluated in the warehouse time zone (Europe/Amsterdam). Weekends and the closed
 * dates from theme settings are skipped. Calculated client-side because Shopify caches pages,
 * which makes 'now' in Liquid unreliable. Re-renders every minute so the message flips at the cutoff.
 */
if (!customElements.get('delivery-promise')) {
  const TIME_ZONE = 'Europe/Amsterdam';
  const DAY_MS = 86400000;

  // Current date (as UTC midnight) and hour in the warehouse time zone.
  const warehouseNow = () => {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .forEach(({ type, value }) => (parts[type] = Number(value)));

    return { date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)), hour: parts.hour };
  };

  const dateKey = (date) => date.toISOString().slice(0, 10);
  const isWeekday = (date) => date.getUTCDay() >= 1 && date.getUTCDay() <= 5;

  const nextMatchingDay = (date, test) => {
    let next = new Date(date.getTime() + DAY_MS);
    for (let i = 0; i < 30 && !test(next); i++) next = new Date(next.getTime() + DAY_MS);
    return next;
  };

  /**
   * One date per line, optionally followed by country codes and a # comment:
   *   2026-12-25 # Kerst        → warehouse closed, no shipping or delivery anywhere
   *   2026-11-11 BE # Wapenstilstand → no delivery in BE only
   */
  const parseClosedDates = (text, country) => {
    const warehouse = new Set();
    const local = new Set();

    (text || '').split('\n').forEach((line) => {
      const [date, ...rest] = line.split('#')[0].trim().split(/[\s,]+/);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;

      const countries = rest.filter((code) => /^[a-z]{2}$/i.test(code)).map((code) => code.toUpperCase());
      if (countries.length === 0) warehouse.add(date);
      else if (countries.includes(country)) local.add(date);
    });

    return { warehouse, local };
  };

  class DeliveryPromise extends HTMLElement {
    connectedCallback() {
      this.render();
      this.timer = setInterval(() => this.render(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    render() {
      const { mode, country, cutoff, closedDates, beforeCutoff, afterCutoff, transit } = this.dataset;
      const closed = parseClosedDates(closedDates, country);
      const isShipDay = (date) => isWeekday(date) && !closed.warehouse.has(dateKey(date));
      const isDeliveryDay = (date) => isShipDay(date) && !closed.local.has(dateKey(date));

      const now = warehouseNow();
      const shipsToday = isShipDay(now.date) && now.hour < Number(cutoff);
      const shipDate = shipsToday ? now.date : nextMatchingDay(now.date, isShipDay);
      const targetDate = mode === 'arrive' ? nextMatchingDay(shipDate, isDeliveryDay) : shipDate;

      const template = shipsToday ? beforeCutoff : afterCutoff;
      const message = template.replace('[day]', this.dayLabel(targetDate, now.date));
      const text = mode === 'arrive' ? message : `${message} · ${transit}`;

      if (this.textContent !== text) this.textContent = text;
    }

    // "morgen" for tomorrow, otherwise the localized weekday ("maandag", "am Montag", ...).
    dayLabel(date, today) {
      if (date.getTime() - today.getTime() === DAY_MS) return this.dataset.tomorrow;

      let weekday;
      try {
        weekday = new Intl.DateTimeFormat(this.dataset.locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
      } catch (error) {
        weekday = new Intl.DateTimeFormat('en', { weekday: 'long', timeZone: 'UTC' }).format(date);
      }
      return this.dataset.weekday.replace('[day]', weekday);
    }
  }

  customElements.define('delivery-promise', DeliveryPromise);
}
